import { useMemo, useState } from "react";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { AlertCircle, Archive, BellOff, Check, Clock, LoaderCircle, MoreHorizontal, Mail, MailOpen } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { EmptyState } from "@/components/EmptyState";
import { Avatar } from "@/components/PropertyIcons";
import { IssueByIdentifier } from "./IssueDetail";
import { TaskById } from "./TaskDetail";
import { errorMessage } from "@/lib/api";
import { formatRelative } from "@/lib/issues";
import { archiveRead, groupByEntity, notificationsQuery, patchNotification, readAll, readEntity, snoozeUntil, type Notification } from "@/lib/inbox";
import { setSubscription } from "@/lib/inbox";
import { useShortcuts } from "@/lib/shortcuts";
import { useProjectStore } from "@/lib/store";
import { toast } from "@/lib/toast";

const route = getRouteApi("/$ws/inbox");
const menuItem = "flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-[13px] outline-none data-[highlighted]:bg-hover";

export function InboxPage() {
  const { t } = useTranslation();
  const { ws } = route.useParams();
  const { me } = route.useRouteContext();
  const search = route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const filter = search.filter === "unread" ? "unread" : "all";
  const q = useQuery(notificationsQuery(ws, filter));
  const groups = useMemo(() => groupByEntity(q.data ?? []), [q.data]);
  const selectedKey = search.n ?? null;
  const selected = groups.find((g) => `${g.head.entity_type}:${g.head.entity_id}` === selectedKey) ?? null;
  const [focusIdx, setFocusIdx] = useState(0);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["notifications", ws] });

  const select = (g: (typeof groups)[number] | null) => {
    void navigate({ to: "/$ws/inbox", params: { ws }, search: { ...(search.filter ? { filter: search.filter } : {}), ...(g ? { n: `${g.head.entity_type}:${g.head.entity_id}` } : {}) }, replace: true });
    if (g && g.unread > 0) void readEntity(ws, g.head.entity_type, g.head.entity_id).then(invalidate);
  };
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Parameters<typeof patchNotification>[1] }) => patchNotification(v.id, v.body),
    onSuccess: invalidate,
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const applyToGroup = (g: (typeof groups)[number], body: Parameters<typeof patchNotification>[1]) => Promise.all(g.all.map((n) => patchNotification(n.id, body))).then(invalidate);

  const archive = (g: (typeof groups)[number]) => {
    void applyToGroup(g, { archived: true }).then(() => {
      toast(t("inbox.archived"), { action: { label: t("common.undo"), run: () => void applyToGroup(g, { archived: false }) } });
      if (selected === g) select(null);
    });
  };
  const toggleRead = (g: (typeof groups)[number]) => void applyToGroup(g, { read: g.unread === 0 ? false : true });
  const snooze = (g: (typeof groups)[number], preset: Parameters<typeof snoozeUntil>[0]) => void applyToGroup(g, { snoozed_until: snoozeUntil(preset) }).then(() => toast(t("inbox.snoozed")));
  const unsubscribe = (g: (typeof groups)[number]) => void setSubscription(g.head.entity_id, false).then(() => toast(t("inbox.unsubscribed")));

  const focused = groups[focusIdx];
  const shortcuts = useMemo(
    () => [
      { keys: "j", run: () => setFocusIdx((i) => Math.min(groups.length - 1, i + 1)) },
      { keys: "k", run: () => setFocusIdx((i) => Math.max(0, i - 1)) },
      { keys: "ArrowDown", run: () => setFocusIdx((i) => Math.min(groups.length - 1, i + 1)) },
      { keys: "ArrowUp", run: () => setFocusIdx((i) => Math.max(0, i - 1)) },
      { keys: "Enter", run: () => focused && select(focused) },
      { keys: "e", run: () => focused && archive(focused) },
      { keys: "Backspace", run: () => focused && archive(focused) },
      { keys: "u", run: () => focused && toggleRead(focused) },
      { keys: "h", run: () => focused && snooze(focused, "tomorrow") },
      { keys: "Escape", run: () => select(null) },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, focused, selected],
  );
  useShortcuts(shortcuts);

  return (
    <>
      <PageHeader
        crumbs={[t("inbox.title")]}
        right={
          <>
            <div className="mr-1 flex h-9 items-center gap-0.5 rounded-full border border-border/60 bg-sidebar/70 p-0.5 text-[12px]">
              {(["all", "unread"] as const).map((f) => (
                <button key={f} aria-pressed={filter === f} className={`h-8 rounded-full px-3.5 font-medium transition-colors ${filter === f ? "bg-elevated text-fg shadow-sm" : "text-muted hover:text-fg"}`} onClick={() => void navigate({ to: "/$ws/inbox", params: { ws }, search: { ...(f === "unread" ? { filter: f } : {}), ...(search.n ? { n: search.n } : {}) }, replace: true })}>
                  {t(`inbox.filter.${f}`)}
                </button>
              ))}
            </div>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button aria-label={t("inbox.more")} title={t("inbox.more")} className="btn btn-ghost h-8 w-8 justify-center rounded-lg p-0"><MoreHorizontal className="h-4 w-4" /></button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={4} className="z-50 w-[220px] rounded-lg border border-border bg-elevated p-1 shadow-xl">
                  <DropdownMenu.Item className={menuItem} onSelect={() => void readAll(ws).then(invalidate)}><Check className="h-3.5 w-3.5" /> {t("inbox.markAllRead")}</DropdownMenu.Item>
                  <DropdownMenu.Item className={menuItem} onSelect={() => void archiveRead(ws).then(invalidate)}><Archive className="h-3.5 w-3.5" /> {t("inbox.archiveRead")}</DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </>
        }
      />
      {q.isPending ? (
        <div role="status" className="flex flex-1 items-center justify-center gap-2.5 text-[13px] text-muted">
          <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
          {t("common.loading")}
        </div>
      ) : q.isError && !q.data ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="flex max-w-sm flex-col items-center gap-4 text-center">
            <AlertCircle className="h-6 w-6 text-muted" aria-hidden="true" />
            <p role="alert" className="text-[14px] leading-6 text-muted">{errorMessage(q.error)}</p>
            <button className="btn h-9 gap-2" disabled={q.isFetching} onClick={() => void q.refetch()}>
              {q.isFetching && <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              {t("common.retry")}
            </button>
          </div>
        </div>
      ) : groups.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <EmptyState title={t("inbox.emptyTitle")} body={filter === "unread" ? t("inbox.emptyUnread") : t("inbox.emptyBody")} />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="flex w-[380px] shrink-0 flex-col overflow-y-auto border-r border-border/60">
            {groups.map((g, i) => (
              <Row
                key={`${g.head.entity_type}:${g.head.entity_id}`}
                g={g}
                actorName={(id: string | null | undefined) => (id ? actorNameFor(id, me.user.id, me.user.name) : null)}
                focused={i === focusIdx}
                selected={selected === g}
                onSelect={() => { setFocusIdx(i); select(g); }}
                onArchive={() => archive(g)}
                onToggleRead={() => toggleRead(g)}
                onSnooze={(p) => snooze(g, p)}
                onUnsubscribe={() => unsubscribe(g)}
                busy={patch.isPending}
              />
            ))}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            {selected ? (
              selected.head.entity_type === "issue" ? (
                <IssueByIdentifier ws={ws} identifier={String(selected.head.payload.identifier ?? "")} embedded />
              ) : selected.head.entity_type === "task" ? (
                <TaskById ws={ws} id={selected.head.entity_id} embedded />
              ) : selected.head.entity_type === "doc" ? (
                <div className="flex flex-1 items-center justify-center p-8"><EmptyState title={String(selected.head.payload.title ?? t("docs.untitled"))} body={String(selected.head.payload.excerpt ?? "")} actions={<Link className="btn btn-primary" to="/$ws/doc/$id" params={{ ws, id: selected.head.entity_id }}>{t("common.open")}</Link>} /></div>
              ) : (
                <div className="p-6 text-[13px] text-muted">{t("inbox.unsupportedEntity")}</div>
              )
            ) : (
              <div className="flex flex-1 items-center justify-center p-6">
                <EmptyState title={t("inbox.selectTitle")} body={t("inbox.selectBody")} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function actorNameFor(id: string, meId: string, meName: string) {
  if (id === meId) return meName;
  // Actors are users from loaded projects; fall back to the id prefix.
  for (const d of Object.values(useProjectStore.getState().byId)) {
    const u = d.users[id];
    if (u) return u.name;
  }
  return null;
}

function Row({ g, actorName, focused, selected, onSelect, onArchive, onToggleRead, onSnooze, onUnsubscribe }: {
  g: ReturnType<typeof groupByEntity>[number];
  actorName: (id: string | null | undefined) => string | null;
  focused: boolean;
  selected: boolean;
  onSelect: () => void;
  onArchive: () => void;
  onToggleRead: () => void;
  onSnooze: (p: "hour" | "tomorrow" | "nextWeek") => void;
  onUnsubscribe: () => void;
  busy: boolean;
}) {
  const { t, i18n } = useTranslation();
  const n = g.head;
  const p = n.payload as Record<string, unknown>;
  const actor = actorName(n.actor_id);
  const line = describe(t, n, actor);
  return (
    <div
      className={`group flex cursor-default gap-3 border-b border-border/50 px-4 py-3 transition-colors ${selected ? "bg-active" : focused ? "bg-hover" : "hover:bg-hover/60"}`}
      onClick={onSelect}
      onMouseEnter={() => undefined}
    >
      <div className="flex w-3 shrink-0 items-start pt-1.5">{g.unread > 0 && <span className="h-2 w-2 rounded-full bg-accent" />}</div>
      <div className="min-w-0 flex-1">
        <div className={`flex items-center gap-2 text-[13px] ${g.unread ? "font-medium text-fg" : "text-fg/90"}`}>
          <span className="shrink-0 font-mono text-[11.5px] text-muted">{String(p.identifier ?? "")}</span>
          <span className="truncate">{String(p.title ?? "")}</span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[12px] text-muted">
          {n.actor_id ? <Avatar user={actor ? { name: actor } : undefined} size={14} /> : <Clock className="h-3.5 w-3.5" />}
          <span className="truncate">{line}</span>
          {g.all.length > 1 && <span className="rounded-full bg-active px-1.5 text-[10.5px]">{g.all.length}</span>}
          <span className="ml-auto shrink-0 text-faint">{formatRelative(n.created_at, i18n.language)}</span>
        </div>
        {typeof p.excerpt === "string" && p.excerpt && <div className="mt-1 truncate text-[12px] text-faint">“{p.excerpt}”</div>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        <button className="rounded p-1 text-muted hover:bg-active hover:text-fg" title={`${g.unread ? t("inbox.markRead") : t("inbox.markUnread")} (U)`} onClick={(e) => { e.stopPropagation(); onToggleRead(); }}>
          {g.unread ? <MailOpen className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
        </button>
        <button className="rounded p-1 text-muted hover:bg-active hover:text-fg" title={`${t("inbox.archive")} (E)`} onClick={(e) => { e.stopPropagation(); onArchive(); }}>
          <Archive className="h-3.5 w-3.5" />
        </button>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="rounded p-1 text-muted hover:bg-active hover:text-fg" aria-label={t("inbox.more")} title={t("inbox.more")} onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content align="end" sideOffset={4} className="z-50 w-[220px] rounded-lg border border-border bg-elevated p-1 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <DropdownMenu.Label className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-faint">{t("inbox.snooze")} (H)</DropdownMenu.Label>
              <DropdownMenu.Item className={menuItem} onSelect={() => onSnooze("hour")}><Clock className="h-3.5 w-3.5" /> {t("inbox.snoozeHour")}</DropdownMenu.Item>
              <DropdownMenu.Item className={menuItem} onSelect={() => onSnooze("tomorrow")}><Clock className="h-3.5 w-3.5" /> {t("inbox.snoozeTomorrow")}</DropdownMenu.Item>
              <DropdownMenu.Item className={menuItem} onSelect={() => onSnooze("nextWeek")}><Clock className="h-3.5 w-3.5" /> {t("inbox.snoozeNextWeek")}</DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              {n.entity_type === "issue" && <DropdownMenu.Item className={menuItem} onSelect={onUnsubscribe}><BellOff className="h-3.5 w-3.5" /> {t("inbox.unsubscribe")}</DropdownMenu.Item>}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </div>
  );
}

function describe(t: (k: string, o?: Record<string, unknown>) => string, n: Notification, actor: string | null): string {
  const p = n.payload as Record<string, unknown>;
  const who = actor ?? t("inbox.someone");
  switch (n.type) {
    case "approval_requested": return t("inbox.types.approval_requested", { who });
    case "assigned": return t("inbox.types.assigned", { who });
    case "mentioned": return t("inbox.types.mentioned", { who });
    case "comment": return t("inbox.types.comment", { who });
    case "comment_reply": return t("inbox.types.comment_reply", { who });
    case "status_changed": return t("inbox.types.status_changed", { who, from: p.from, to: p.to });
    case "priority_changed": return t("inbox.types.priority_changed", { who });
    case "due_soon": return p.overdue ? t("inbox.types.overdue") : t("inbox.types.due_soon");
    case "task_member": return t("inbox.types.task_member", { who });
    case "task_updated": return t("inbox.types.task_updated", { who, health: t(`health.${p.health}`) });
    case "milestone_due": return p.overdue ? t("inbox.types.milestone_overdue") : t("inbox.types.milestone_due", { date: p.target_date });
    default: return n.type;
  }
}
